import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container, Button } from "@mui/material";
import { trackScreenView } from '../lib/gtag.js';
import { fireGAEvent } from '../lib/gtag'; 

export default function BBFIntro() {
  const sectionRef = useRef();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const detectPlatform = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      if (/android/i.test(ua)) return 'android';
      if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
      return 'desktop';
    };

    const platform = detectPlatform();

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasTrackedRef.current) {
        trackScreenView(`BBFIntro::${platform}`);
        hasTrackedRef.current = true;
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.3 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleDownloadClick = () => {
    window.__skipNextTracking = true;

    setTimeout(() => {
      window.__skipNextTracking = false;
    }, 1000);

    fireGAEvent("download_click", {
      event_category: "CTA",
      event_label: "Download Butler Brief",
      screen_name: "BBFIntro::Button::DOWNLOAD THE BUTLER BRIEF",
      value: 1,
      version: "1.0.0",
    });

    const target = document.getElementById("download");
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    } else {
      //console.warn("Download section not found: #download");
    }
  };

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{ py: { xs: 6, md: 10 }, bgcolor: "#002B45", color: "#ffffff" }}
    >
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom>
          A Criminal Complaint Has Been Filed
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom>
          Joe Biden and the Untouchables Are Now Named as Defendants.
        </Typography>

        <Box
          sx={{
            mt: 4,
            mb: 4,
            pl: 2,
            borderLeft: "4px solid #ccc",
            bgcolor: "#f5f2ea",
            color: "#111",
          }}
        >
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            And yes — we name names.
          </Typography>
          <Typography variant="body1" paragraph>
            The signers of the “Russian disinformation” letter — the one used to bury Hunter Biden’s laptop story — are now named as felons.
          </Typography>
          <Typography variant="body1" paragraph>
            We expose the cover-up of the Butler Bullseye.
          </Typography>
          <Typography variant="body1" paragraph>
            And we show how life in prison is suddenly on the table, for many.
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{ fontStyle: 'italic', color: '#666' }}
          >
            (You’ll see for yourself soon.)
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          A criminal complaint has been filed against Joseph R. Biden Jr. and more than 120 named co-conspirators — including senior figures from his administration, the military, Big Tech, and major media outlets.
        </Typography>

        <Typography variant="body1" paragraph>
          The charges are not limited to corruption or concealment. They include:
        </Typography>

        <ul style={{ paddingLeft: "1.5rem", marginTop: 0, marginBottom: "1.5rem" }}>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Three coordinated assassination attempts</strong> against Donald J. Trump
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>A conspiracy leading to the 10/7 massacre in Israel</strong> — a “9/11-style” attack with U.S. fingerprints
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>A sustained pattern of criminal behavior</strong> to collapse the border, rig elections, and silence dissent
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Knowingly aiding Iran’s path to nuclear weapons</strong> — with full awareness of the consequences
            </Typography>
          </li>
        </ul>

        <Typography variant="body1" paragraph>
          This complaint, the Butler Brief, is not a partisan witch hunt. It is forensic — and it names those who believed they were above the law, including many whose job it was to enforce the law:
        </Typography>

        <Typography variant="body1" paragraph>
          Anthony Fauci. Mark Milley. Alejandro Mayorkas. Merrick Garland. Christopher Wray. James Comey.
        </Typography>

        <Typography variant="body1" paragraph>
          Even former President Barack Obama — now a named defendant — once warned:
        </Typography>

        <Typography
          variant="h6"
          component="blockquote"
          sx={{ fontStyle: "italic", my: 2, lineHeight: 1.4 }}
        >
          “Don’t underestimate Joe’s ability to f**k things up.”
        </Typography>

        <Typography variant="body1" paragraph>
          He wasn’t wrong. But it wasn’t just incompetence. It was over-exposure.
        </Typography>

        <Typography variant="body1" paragraph>
          Loose lips sink ships.
        </Typography>

        <Typography variant="body1" paragraph>
          Joe Biden — and Hunter, too — gave away more than just “the secrets of their success.” They negligently exposed the criminal conspiracy that created them. A criminal conspiracy so vast, and so dark, it may finally bring down even the untouchables:
        </Typography>

        <Typography variant="body1" paragraph>
          Bush. Clinton. Cheney. Soros. Zuckerberg. Obama. (The Butler Brief names over 120)
        </Typography>

        <Typography variant="body1" paragraph>
          …and others who thought they got away with murder.
        </Typography>

        <Button
          variant="contained"
          color="error"
          size="large"
          sx={{
            mt: 4,
            px: 4,
            py: 2,
            fontSize: "1rem",
            fontWeight: "bold",
            borderRadius: 2,
            boxShadow: 3,
            textTransform: "uppercase",
          }}
          onClick={handleDownloadClick}
        >
          DOWNLOAD THE BUTLER BRIEF
        </Button>
      </Container>
    </Box>
  );
}